import React, { useEffect, useMemo, useState } from 'react';
import { MdCheck, MdKeyboardArrowDown, MdOutlineChatBubbleOutline } from 'react-icons/md';
import FillAppField from 'components/FillAppField';
import RepeatingGroup from 'components/RepeatingGroup';
import {
    type FillAppField as FieldDef,
    type FillAppSubtab as SubtabDef,
    type FillAppFieldGroup,
    type ChoiceGroups,
    type FillAppValues,
    fieldIsVisible,
    subtabGroups,
    groupFieldsOf,
    groupState,
    groupSummary,
    groupCommentCount,
} from 'global/fillapp';

import './index.css';

type Props = {
    subtab: SubtabDef;
    values: FillAppValues;
    choiceGroups: ChoiceGroups;
    readOnly?: boolean;
    onChange?: (qid: string, value: unknown) => void;
    // Open (unresolved) comment counts per qid — drives the card badges.
    openCommentsByQid?: Record<string, number>;
};

// Summary-first cards, tutor flavor: reviewers need to scan what the student
// actually did, so cards with any content OR open comments start expanded and
// empty ones start collapsed ("Not started"). Multiple cards can be open;
// an Expand/Collapse all toolbar covers both extremes.
function FillAppSubtab({ subtab, values, choiceGroups, readOnly, onChange, openCommentsByQid }: Props) {
    const cards = subtabGroups(subtab);

    const rgroups = subtab.repeatingGroups ?? [];
    const fieldsByQid: Record<string, FieldDef> = {};
    subtab.fields.forEach(f => { fieldsByQid[f.qid] = f; });

    const groupByQid = new Map<string, string>();
    const groupAnchor = new Map<string, string>();
    const groupById = new Map(rgroups.map(g => [g.id, g]));
    rgroups.forEach(g => {
        const members = [...g.sharedQids, ...g.slots.flat(), ...g.trailingSharedQids];
        members.forEach(q => groupByQid.set(q, g.id));
        const anchor = subtab.fields.find(f => members.includes(f.qid));
        if (anchor) groupAnchor.set(g.id, anchor.qid);
    });

    // Smart defaults: expanded = has content or open comments. Recomputed per
    // subtab switch only — manual toggles stick while reviewing.
    const defaultExpanded = useMemo(() => {
        const open = new Set<string>();
        for (const g of cards) {
            const hasComments = openCommentsByQid ? groupCommentCount(g, openCommentsByQid) > 0 : false;
            if (hasComments || groupState(subtab, g, values) !== 'empty') open.add(g.id);
        }
        return open;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subtab.id]);
    const [expanded, setExpanded] = useState<Set<string>>(defaultExpanded);
    useEffect(() => { setExpanded(defaultExpanded); }, [defaultExpanded]);

    const toggle = (id: string) => setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const renderFields = (group: FillAppFieldGroup) => {
        const renderedGroups = new Set<string>();
        return groupFieldsOf(subtab, group).map(field => {
            const gid = groupByQid.get(field.qid);
            if (gid) {
                if (groupAnchor.get(gid) !== field.qid || renderedGroups.has(gid)) return null;
                renderedGroups.add(gid);
                return (
                    <RepeatingGroup
                        key={`group-${gid}`}
                        group={groupById.get(gid)!}
                        fieldsByQid={fieldsByQid}
                        values={values}
                        choiceGroups={choiceGroups}
                    />
                );
            }
            if (!fieldIsVisible(field, values)) return null;
            return (
                <FillAppField
                    key={field.qid}
                    field={field}
                    value={values[field.qid]}
                    values={values}
                    choiceGroups={choiceGroups}
                    readOnly={readOnly}
                    onChange={onChange}
                />
            );
        });
    };

    return (
        <div className='fsubtab'>
            {cards.length > 1 && (
                <div className='fsubtab__tools'>
                    <button type='button' onClick={() => setExpanded(new Set(cards.map(g => g.id)))}>Expand all</button>
                    <span>·</span>
                    <button type='button' onClick={() => setExpanded(new Set())}>Collapse all</button>
                </div>
            )}
            {cards.map(group => {
                const anyVisible = groupFieldsOf(subtab, group).some(f =>
                    groupByQid.has(f.qid) || fieldIsVisible(f, values));
                if (!anyVisible) return null;

                const state = groupState(subtab, group, values);
                const isOpen = expanded.has(group.id);
                const summary = isOpen ? '' : groupSummary(subtab, group, values, choiceGroups);
                const commentCount = openCommentsByQid ? groupCommentCount(group, openCommentsByQid) : 0;

                return (
                    <div
                        key={group.id}
                        id={`fgcard-${group.id}`}
                        className={`fgcard ${isOpen ? 'fgcard--open' : ''}`}
                    >
                        <button type='button' className='fgcard__head' onClick={() => toggle(group.id)}>
                            <span className={`fgcard__state fgcard__state--${state}`}>
                                {state === 'complete' && <MdCheck size={13} />}
                            </span>
                            <span className='fgcard__headMain'>
                                <span className='fgcard__title'>{group.title}</span>
                                {!isOpen && (
                                    summary
                                        ? <span className='fgcard__summary'>{summary}</span>
                                        : <span className='fgcard__summary fgcard__summary--empty'>Not started</span>
                                )}
                            </span>
                            {commentCount > 0 && (
                                <span className='fgcard__cmt'>
                                    <MdOutlineChatBubbleOutline size={11} /> {commentCount}
                                </span>
                            )}
                            <span className='fgcard__affordance'>
                                <MdKeyboardArrowDown size={19} className={`fgcard__chev ${isOpen ? 'fgcard__chev--up' : ''}`} />
                            </span>
                        </button>
                        {isOpen && <div className='fgcard__body'>{renderFields(group)}</div>}
                    </div>
                );
            })}
        </div>
    );
}

export default FillAppSubtab;
